// Sentry init — gated by env.sentryDsn. If the DSN isn't set (local dev,
// or CF Pages env var missing), every Sentry call becomes a no-op and the
// ErrorBoundary still works (just logs to console).
//
// Why a wrapper instead of inlining: keeps main.tsx readable and lets the
// rest of the code call `reportError(e)` without importing Sentry directly.
// Future swap (e.g., to Firestore /errors log) is a one-file change.

import * as Sentry from "@sentry/react";
import { env } from "./env";

let initialized = false;

export function initSentry(): void {
  if (initialized) return;
  if (!env.sentryDsn) return;
  Sentry.init({
    dsn: env.sentryDsn,
    release: env.release,
    environment: env.useEmulators ? "emulator" : "production",
    // Sample 10% of route transitions for performance traces — enough to
    // spot slow Player mount / Map first paint without burning quota.
    tracesSampleRate: 0.1,
    // No replay / session tracking — we don't need to record 25 phones'
    // entire sessions, only the moments things blow up.
    integrations: [],
    // Strip query strings (auth tokens) and PII from event URLs.
    beforeBreadcrumb(crumb) {
      if (crumb.data && typeof crumb.data === "object") {
        const data = crumb.data as Record<string, unknown>;
        if (typeof data.url === "string") {
          try {
            const u = new URL(data.url);
            u.search = "";
            data.url = u.toString();
          } catch {
            /* ignore */
          }
        }
      }
      return crumb;
    },
  });
  initialized = true;
}

/** Forward an error to Sentry if configured; always logs to console too. */
export function reportError(
  error: unknown,
  context?: Record<string, unknown>,
): void {
  // eslint-disable-next-line no-console
  console.error("[reportError]", error, context);
  if (!initialized) return;
  Sentry.captureException(error, context ? { extra: context } : undefined);
}

/** Attach a userId so error events are grouped per learner. */
export function setSentryUser(userId: string | null): void {
  if (!initialized) return;
  Sentry.setUser(userId ? { id: userId } : null);
}
